/** QuickRouter — Barre d'outils du schéma : outils, symbole d'alimentation, ERC, export. */
import { useMemo, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { runErc } from '../../shared/schematic.js';
import { renderSchematicSvg } from '../../shared/render-sch-svg.js';

const POWERS = ['GND', '+3V3', '+5V', '+12V', '+24V', 'VIN'];

function download(name, data, type) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([data], { type }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export default function SchToolbar() {
  const project = useProjectStore((s) => s.project);
  const tool = useProjectStore((s) => s.schTool);
  const setTool = useProjectStore((s) => s.setSchTool);
  const power = useProjectStore((s) => s.schPower);
  const setPower = useProjectStore((s) => s.setSchPower);
  const showErc = useProjectStore((s) => s.show.drc);
  const toggle = useProjectStore((s) => s.toggleShow);
  const fit = useProjectStore((s) => s.fit);
  const ro = project.role === 'viewer';
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const lookup = useProjectStore.getState().lookup();
  const erc = useMemo(() => runErc(project, lookup), [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const slug = project.name.replace(/[^\w.-]+/g, '_').slice(0, 60) || 'schema';

  const exportSvg = () => download(`${slug}-schema.svg`, renderSchematicSvg(project, lookup, { scale: 4 }), 'image/svg+xml');
  /** PDF : la feuille s'ouvre dans une fenêtre d'impression au format A3 paysage. */
  const exportPdf = () => {
    const svg = renderSchematicSvg(project, lookup, { scale: 4 });
    const w = window.open('', '_blank');
    if (!w) { useProjectStore.getState().setStatus('Fenêtre bloquée : autorisez les fenêtres surgissantes pour imprimer le schéma'); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${project.name} — schéma</title><style>@page{size:A3 landscape;margin:8mm}html,body{margin:0}svg{width:100%;height:auto;display:block}</style></head><body>${svg}<script>window.onload=()=>{window.print()}</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="toolbar sch-toolbar">
      <div className="seg">
        <button className={tool === 'select' ? 'active' : ''} onClick={() => setTool('select')} title="Sélection / déplacement — R tourne, M miroir, Suppr (S ou Échap)">↖ Sélection</button>
        <button className={tool === 'wire' ? 'active' : ''} disabled={ro} onClick={() => setTool('wire')} title="Tracer un fil (W)">╱ Fil</button>
        <button className={tool === 'label' ? 'active' : ''} disabled={ro} onClick={() => setTool('label')} title="Étiquette de net sur une broche ou un fil (L)">🏷 Étiquette</button>
        <button className={tool === 'power' ? 'active' : ''} disabled={ro} onClick={() => setTool('power')} title="Symbole d'alimentation ou de masse (P)">⏚ Alim.</button>
      </div>
      {tool === 'power' && (
        <div className="seg">
          {POWERS.map((p) => <button key={p} className={power === p ? 'active' : ''} onClick={() => setPower(p)}>{p}</button>)}
          <input className="pw-custom" placeholder="autre…" value={custom} onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter' && custom.trim()) { setPower(custom.trim()); setCustom(''); } }} />
        </div>
      )}
      <span className="grow" />
      <div className="erc-wrap">
        <button onClick={() => setOpen(!open)} className={erc.errors.length ? 'erc-ko' : 'erc-ok'} title="Contrôle électrique du schéma">
          {erc.errors.length ? `✗ ERC ${erc.errors.length}` : '✓ ERC'}{erc.warnings.length ? ` · ⚠ ${erc.warnings.length}` : ''}
        </button>
        {open && (
          <div className="erc-panel">
            <div className="row"><b className="grow">Contrôle électrique</b><label className="muted"><input type="checkbox" checked={showErc} onChange={() => toggle('drc')} /> marques</label><button className="icon" onClick={() => setOpen(false)}>✕</button></div>
            {!erc.errors.length && !erc.warnings.length && <div className="muted">Aucun défaut.</div>}
            <ul className="issues">
              {erc.errors.map((e, i) => <li key={`e${i}`} className="err">{e.message}</li>)}
              {erc.warnings.map((e, i) => <li key={`w${i}`} className="warn">{e.message}</li>)}
            </ul>
          </div>
        )}
      </div>
      <button onClick={exportSvg} title="Télécharger le schéma en SVG">⤓ SVG</button>
      <button onClick={exportPdf} title="Imprimer ou enregistrer le schéma en PDF (A3 paysage)">⎙ PDF</button>
      <button className="icon" onClick={fit} title="Cadrer le schéma (F)">⤢</button>
    </div>
  );
}
